import CustomForm from "../CustomForm";
import ICustomFormSettings from "../ICustomFormSettings";
import CheckboxCell from "../shared/components/FormCell/CheckboxCell";
import {CheckboxItem} from "../../model/items/checkbox";
import {TextboxItem} from "../../model/items/textbox";
import moment, {Moment} from "moment";
import 'moment/locale/cs';
import 'moment/locale/th';

import {Vnode} from "mithril";
import {LabelItem} from "../../model/items/labelItem";
import {key as LocaleKeyType} from "flatpickr/dist/types/locale";

type CalendarConfig = {
    range: "datetime"|"day"|"week"|"custom",
    minDate: null|Moment,
    maxDate: null|Moment,
    locale: LocaleKeyType|undefined
};

export default abstract class Calendar extends CustomForm {

    protected beginDateInput!: TextboxItem;
    protected endDateInput!: TextboxItem;

    protected jsonError: string = '';

    protected calendarConfig: CalendarConfig = {
        range: 'day',
        minDate: null,
        maxDate: null,
        locale: undefined
    };

    protected checkboxes: CheckboxCell[] = [];

    constructor(settings: ICustomFormSettings) {
        super(settings);

        let labelWithConfig;

        for (const cell of this.settings.form.grid.cells) {
            for (let cellItem of cell.cellItems) {
                if (cellItem instanceof CheckboxItem) {
                    this.checkboxes.push( new CheckboxCell(
                        cell,
                        (ci, v) => {
                            this.onSetValue(ci, v);
                        },
                    ));
                } else if (cellItem instanceof TextboxItem) {
                    if (!this.beginDateInput) {
                        this.beginDateInput = cellItem;
                    } else {
                        this.endDateInput = cellItem;
                    }
                } else if (cellItem instanceof LabelItem) {
                    labelWithConfig = cellItem;
                }
            }
        }

        // Calendar config
        if (labelWithConfig) {
            try {
                const config = this.calendarConfig = this.loadConfig(labelWithConfig);

                const ranges = ['datetime', 'day', 'week', 'custom'];
                if (config.range && ranges.indexOf(config.range) === -1) {
                    throw Error(`Range must be one of: ${ranges.join(', ')}, got '${config.range}'.`);
                }
                this.calendarConfig.range = config.range.toLowerCase();

                if (config.allowLastDays && config.allowLastDays > 0) { // allow to pick only last x days
                    this.calendarConfig.minDate = moment().subtract(config.allowLastDays, 'days');
                    this.calendarConfig.minDate.hour(0).minute(0);
                    this.calendarConfig.maxDate = this.parseDate('today');
                    this.calendarConfig.maxDate.hour(23).minute(59).second(59);
                } else {
                    if (config.beginDate) { // or allow date range
                        this.calendarConfig.minDate = this.parseDate(config.beginDate);
                        this.calendarConfig.minDate.hour(0).minute(0);
                    }

                    if (config.endDate) {
                        this.calendarConfig.maxDate = this.parseDate(config.endDate);
                        this.calendarConfig.maxDate.hour(23).minute(59).second(59);
                    }
                }


                this.calendarConfig.locale = config.locale || undefined;
            } catch (e) {
                this.jsonError = e.message;
            }
        }

    }

    private loadConfig(label: LabelItem) {
        let labelText = label.childrenTags[0]?.value || '{}';
        if (labelText[0] !== '{') {
            labelText = this.settings.form.questionary.renderText(labelText);
        }
        return JSON.parse(labelText);
    }

    /**
     * Parse string date from configuration
     */
    protected parseDate(date: string): Moment {
        if (date === 'today') {
            return moment();
        }
        if ( /^today([+\-])\d+$/g.test(date)) {
            const daysDiff = Number.parseInt(date.substr(5));
            return moment().add(daysDiff, "days");
        }
        const res = moment(date, 'YYYY-MM-DD');
        if (!res.isValid()) {
            throw Error(`Expect endDate in format YYYY-MM-DD or 'today', got '${date}'.`);
        }
        return res;
    }

    public rewriteFormControls(): boolean {
        return true;
    }

    checkRequirements(): boolean {

        if (!this.beginDateInput || !this.endDateInput) {
            console.log('customForm-Calendar: Missing TextBox fields for saving range.');
            return false;
        }

        if (this.jsonError) {
            console.log('customForm-Calendar: Problems with calendar configuration via label: ');
            return false;
        }


        return true;
    }

    abstract view(): Vnode;
}